import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, LogOut, Plus, ChevronLeft, ChevronRight, QrCode,
  Copy, CheckCircle, AlertCircle, ClipboardList,
  Building2, Trash2, Edit2, RefreshCw, Bell, Menu, X,
  CheckSquare, XSquare, BarChart3, Trophy, Award,
  Save, ImageIcon, Settings, UserMinus, Send,
  Headphones, Image, ShieldCheck, Clock, ShieldAlert,
  Download, FileText, Users, ClipboardCheck, Video, User, Camera
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { eventService, eventAccessService, judgeService, imageService, levelService, eventGymnastService } from '../services/supabaseService';
import { supabase } from '../config/supabase';
import { 
  eventClubServiceProxy, 
  gymnastServiceProxy, 
  imageServiceProxy, 
  eventLevelServiceProxy,
  coachClubAccessServiceProxy,
  organizerServiceProxy,
  storageServiceProxy
} from '../services/proxyService';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../context/AuthContext';
import DisciplineSelector from '../components/shared/DisciplineSelector';
import ApparatusSelector from '../components/shared/ApparatusSelector';
import LevelSelectorSimple from '../components/shared/LevelSelectorSimple';
import JudgeInvitationManager from '../components/admin/JudgeInvitationManager';
import ClubInvitationManager from '../components/admin/ClubInvitationManager';
import OrganizerEventAthletes from '../components/organizer/OrganizerEventAthletes';
import { generateResultsPDF, generateClubsPDF, generateClubsWithAthletesPDF, generateJudgeScoreSheetsPDF } from '../utils/pdfGenerator';
import { COMPETITION_LEVELS, JUDGES_CONFIG_BY_LEVEL } from '../utils/constants';
import { useConfirm } from '../components/shared/ConfirmContext';
import { useNotifications } from '../components/shared/useNotifications';
import ClubTopbar from '../components/club/ClubTopbar';
import './OrganizerDashboard.css';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const CHART_COLORS = ['#7c3aed', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const APPARATUS_NAMES = {
  salto: 'Salto', viga: 'Viga de Equilibrio', suelo: 'Suelo',
  barras_asimetricas: 'Barras Asimétricas', barras_paralelas: 'Barras Paralelas',
  barra_fija: 'Barra Fija', caballo: 'Caballo con Arzones', anillas: 'Anillas',
  aro: 'Aro', pelota: 'Pelota', mazas: 'Mazas', cinta: 'Cinta',
  all_around: 'All Around', team: 'Equipo',
  balance: 'Ejercicio de Balance', dynamic: 'Ejercicio Dinámico', combined: 'Ejercicio Combinado',
  individual_m: 'Individual Masculino', individual_f: 'Individual Femenino',
  sync_m: 'Sincronizado Masculino', sync_f: 'Sincronizado Femenino',
  tumbling_m: 'Tumbling Masculino', tumbling_f: 'Tumbling Femenino',
  floor_teamgym: 'Suelo TeamGym', tumbling_teamgym: 'Tumbling TeamGym',
  mini_tramp_teamgym: 'Mini Trampolín TeamGym', display: 'Exhibición',
  speed_m: 'Speed Run Masculino', speed_f: 'Speed Run Femenino',
  freestyle_m: 'Freestyle Masculino', freestyle_f: 'Freestyle Femenino',
  '5_aros': '5 Aros', mix_cinta_pelota: '3 Cintas + 2 Pelotas',
  pairs: 'Parejas Mixtas', trios: 'Tríos', groups: 'Grupos',
  dmt_m: 'Doble Mini Masculino', dmt_f: 'Doble Mini Femenino',
  vt: 'Salto', ub: 'Barras Asimétricas', bb: 'Viga', fx: 'Suelo',
  ph: 'Caballo con Arzones', sr: 'Anillas', pb: 'Barras Paralelas', hb: 'Barra Fija',
  hoop: 'Aro', ball: 'Pelota', clubs: 'Mazas', ribbon: 'Cinta'
};
const getApparatusName = (code) => APPARATUS_NAMES[code] || code || 'Aparato';

const CATEGORY_TABS = [
  { key: 'acrobatic', discipline: 'acrobatic', category: '', name: 'ACRO', fullName: 'Gimnasia Acrobática', logo: '/acro.png' },
  { key: 'artistic-wag', discipline: 'artistic', category: 'wag', name: 'GAF', fullName: 'Artística Femenina', logo: '/gaf.png' },
  { key: 'artistic-mag', discipline: 'artistic', category: 'mag', name: 'GAM', fullName: 'Artística Masculina', logo: '/gam.png' },
  { key: 'gymnastics-for-all', discipline: 'gymnastics_for_all', category: '', name: 'GPT', fullName: 'Gimnasia Para Todos', logo: '/gpt.png' },
  { key: 'rhythmic', discipline: 'rhythmic', category: '', name: 'GR', fullName: 'Gimnasia Rítmica', logo: '/gr.png' },
  { key: 'trampoline', discipline: 'trampoline', category: '', name: 'GT', fullName: 'Gimnasia de Trampolín', logo: '/gt.png' },
  { key: 'aerobic', discipline: 'aerobic', category: '', name: 'AER', fullName: 'Aeróbica', logo: '/aer.png' },
  { key: 'parkour', discipline: 'parkour', category: '', name: 'PK', fullName: 'Parkour', logo: '/pk.png' },
];

const SIDEBAR_ITEMS = [
  { key: 'profile', label: 'Mi Perfil', icon: User, color: '#6366f1' },
  { key: 'events', label: 'Mis Copas', icon: Calendar, color: '#3b82f6' },
  { key: 'create', label: 'Crear Copa', icon: Plus, color: '#10b981' },
  { key: 'codes', label: 'Códigos / QR', icon: QrCode, color: '#8b5cf6' },
  { key: 'solicitudes', label: 'Solicitudes', icon: Bell, color: '#f59e0b' },
  { key: 'event-athletes', label: 'Inscripciones', icon: Award, color: '#14b8a6' },
  { key: 'invitations', label: 'Invitaciones', icon: Send, color: '#ec4899' },
  { key: 'downloads', label: 'Descargables', icon: Download, color: '#e55d36' },
  { key: 'results', label: 'Resultados', icon: BarChart3, color: '#7c3aed' },
];

const OrganizerDashboard = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const notify = useNotifications();
  const { profile, signOut, isAuthenticated, loading: authLoading, updatePassword } = useAuth();
  const [organizer, setOrganizer] = useState(null);
  const [myEvents, setMyEvents] = useState([]);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('events');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Event form
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '', date: '', location: '', discipline: '', category: '',
    apparatus: [], selectedLevels: [], is_active: true,
    live_support: false, flyer_service: false, streaming_service: false,
    imageFile: null, imagePreview: null,
    rulesPdfFile: null, rulesPdfPreview: null,
    competition_level: 'particular',
    judges_config: {},
    use_trim_mean: true,
    federated_only: false
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // QR Code modal
  const [qrModal, setQrModal] = useState(null);
  const [codeLoading, setCodeLoading] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  // Club access requests
  const [accessRequests, setAccessRequests] = useState([]);

  // Manage event modal
  const [managingEvent, setManagingEvent] = useState(null);
  const [managingRequests, setManagingRequests] = useState([]);
  const [managingLoading, setManagingLoading] = useState(false);

  // Solicitudes - event filter
  const [solicitudesEventFilter, setSolicitudesEventFilter] = useState('');
  // Club logo cache
  const [clubLogoMap, setClubLogoMap] = useState({});
  // Club detail modal
  const [clubDetailModal, setClubDetailModal] = useState(null); // { club, logo, athleteCount, coachCount }
  const [clubDetailLoading, setClubDetailLoading] = useState(false);

  // Results / Analytics
  const [resultsEventId, setResultsEventId] = useState('');
  const [resultsData, setResultsData] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  // Event Athletes view
  const [athletesEventId, setAthletesEventId] = useState('');
  const [viewingEventAthletes, setViewingEventAthletes] = useState(null);

  // Invitations unified tab
  const [invitationTab, setInvitationTab] = useState('judges');

  // Section transition
  const [sectionVisible, setSectionVisible] = useState(true);

  // Downloads section
  const [downloadsEventId, setDownloadsEventId] = useState('');
  const [downloadsEvent, setDownloadsEvent] = useState(null);
  const [downloadsEventLevels, setDownloadsEventLevels] = useState([]);
  const [downloadsClubSummary, setDownloadsClubSummary] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [selectedScoreApparatus, setSelectedScoreApparatus] = useState('');
  const [selectedScoreLevel, setSelectedScoreLevel] = useState('');
  const [selectedScoreFranja, setSelectedScoreFranja] = useState('');

  // Profile states
  const [profileData, setProfileData] = useState({ name: '', phone: '', location: '', organization: '' });
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);

  // Verify session
  useEffect(() => {
    if (authLoading) return;
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setOrganizer({ id: profile.id, name: profile.name, email: profile.email });
    setProfileData({ name: profile.name || '', phone: profile.phone || '', location: profile.location || '', organization: profile.organization || '' });
    // Load photo from images table
    (async () => {
      const { data: photoUrl } = await imageServiceProxy.getByEntity('organizer', profile.id);
      if (photoUrl) setProfilePhotoUrl(photoUrl);
    })();
  }, [authLoading, profile?.id]);

  // Load data
  const loadData = useCallback(async () => {
    if (!organizer) return;
    setLoading(true);
    try {
      const [eventsRes, judgesRes] = await Promise.all([
        eventService.getByCreator(organizer.id),
        judgeService.getAll()
      ]);
      const events = eventsRes.data || [];
      setMyEvents(events);
      if (judgesRes.data) setJudges(judgesRes.data);

      if (events.length > 0) {
        const eventIds = events.map(e => e.id);
        const { data: requests } = await eventAccessService.getRequestsByEvents(eventIds);
        setAccessRequests(requests || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [organizer]);

  useEffect(() => {
    if (organizer) loadData();
  }, [organizer, loadData]);

  // Month filtering
  const filteredEvents = useMemo(() => {
    return myEvents.filter(event => {
      if (!event.date) return false;
      const [y, m] = event.date.split('-');
      return parseInt(y) === selectedYear && parseInt(m) === selectedMonth + 1;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [myEvents, selectedMonth, selectedYear]);

  const handlePrevMonth = () => {
    if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1); }
    else { setSelectedMonth(m => m - 1); }
  };
  const handleNextMonth = () => {
    if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1); }
    else { setSelectedMonth(m => m + 1); }
  };
  const handleGoToCurrentMonth = () => {
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  // Image upload
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setEventForm(prev => ({ ...prev, imageFile: file, imagePreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };
  const clearImagePreview = () => {
    setEventForm(prev => ({ ...prev, imageFile: null, imagePreview: null }));
  };

  // Rules PDF handling
  const handleRulesPdfChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { notify.warning('Solo se permiten archivos PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { notify.warning('El archivo no puede superar 10MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      setEventForm(prev => ({ ...prev, rulesPdfFile: file, rulesPdfPreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };
  const clearRulesPdfPreview = () => {
    setEventForm(prev => ({ ...prev, rulesPdfFile: null, rulesPdfPreview: null }));
  };

  // Event CRUD
  const handleCreateEvent = async () => {
    setFormError('');
    setFormLoading(true);
    if (!eventForm.name.trim() || !eventForm.date) {
      setFormError('Nombre y fecha son obligatorios');
      setFormLoading(false);
      return;
    }
    try {
      const eventData = {
        name: eventForm.name, date: eventForm.date, location: eventForm.location,
        discipline: eventForm.discipline, category: eventForm.category,
        apparatus: eventForm.apparatus, is_active: eventForm.is_active,
        live_support: eventForm.live_support, flyer_service: eventForm.flyer_service,
        streaming_service: eventForm.streaming_service,
        competition_level: eventForm.competition_level || 'particular',
        judges_config: eventForm.judges_config || {},
        use_trim_mean: eventForm.use_trim_mean !== false,
        federated_only: eventForm.federated_only || false,
        created_by: organizer.id, created_by_role: 'organizer'
      };

      let result;
      if (editingEvent) {
        result = await eventService.update(editingEvent.id, eventData);
      } else {
        result = await eventService.create(eventData);
        if (result.data) {
          await eventAccessService.generateEventCode(result.data.id);
          // Send notification email to admin
          try {
            await fetch('/api/send-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'event-approval',
                eventName: eventForm.name,
                eventDate: eventForm.date,
                eventLocation: eventForm.location,
                discipline: eventForm.discipline,
                organizerName: organizer.name,
                organizerEmail: organizer.email,
                organizerOrg: organizer.organization,
                liveSupport: eventForm.live_support,
                flyerService: eventForm.flyer_service,
                streamingService: eventForm.streaming_service
              })
            });
          } catch (emailErr) {
            console.warn('No se pudo enviar notificación al admin:', emailErr);
          }
        }
      }

      // Upload image after event is created/updated (we need the event ID)
      if (!result.error && eventForm.imageFile) {
        const eventId = editingEvent ? editingEvent.id : result.data?.id;
        if (eventId) {
          setUploadingImage(true);
          await imageService.uploadImage(eventForm.imageFile, 'event', eventId);
          setUploadingImage(false);
        }
      }

      // Upload rules PDF
      if (!result.error && eventForm.rulesPdfFile) {
        const eventId = editingEvent ? editingEvent.id : result.data?.id;
        if (eventId) {
          try {
            const fileName = `events/rules-${eventId}-${Date.now()}.pdf`;
            const uploadResult = await storageServiceProxy.upload('gymnastics-images', fileName, eventForm.rulesPdfFile);
            if (uploadResult.success && uploadResult.data?.publicUrl) {
              await eventService.update(eventId, { rules_pdf_url: uploadResult.data.publicUrl });
            }
          } catch (pdfErr) {
            console.error('Error uploading rules PDF:', pdfErr);
          }
        }
      }

      // Save event levels
      if (!result.error) {
        const eventId = editingEvent ? editingEvent.id : result.data?.id;
        if (eventId && eventForm.selectedLevels && eventForm.selectedLevels.length > 0) {
          try {
            await levelService.setEventLevels(eventId, eventForm.selectedLevels);
          } catch (lvlErr) {
            console.error('Error guardando niveles del evento:', lvlErr);
          }
        } else if (eventId && (!eventForm.selectedLevels || eventForm.selectedLevels.length === 0)) {
          // If no levels selected, clear existing ones
          try {
            await eventLevelServiceProxy.deleteByEvent(eventId);
          } catch (lvlErr) {
            console.error('Error limpiando niveles del evento:', lvlErr);
          }
        }
      }

      if (result.error) {
        setFormError(result.error);
      } else {
        resetEventForm();
        await loadData();
        setActiveSection('events');
      }
    } catch (err) {
      setFormError('Error al guardar la copa');
    } finally {
      setFormLoading(false);
      setUploadingImage(false);
    }
  };

  const resetEventForm = () => {
    setEditingEvent(null);
    setEventForm({
      name: '', date: '', location: '', discipline: '', category: '',
      apparatus: [], selectedLevels: [], is_active: true,
      live_support: false, flyer_service: false, streaming_service: false,
      imageFile: null, imagePreview: null,
      competition_level: 'particular',
      judges_config: {},
      use_trim_mean: true
    });
    setFormError('');
  };

  const handleEditEvent = async (event) => {
    setEditingEvent(event);
    // Load saved levels from event_levels table
    let savedLevelIds = [];
    try {
      const { data: eventLevels } = await levelService.getEventLevels(event.id);
      if (eventLevels && eventLevels.length > 0) {
        savedLevelIds = eventLevels.map(el => el.level_id);
      }
    } catch (err) {
      console.error('Error cargando niveles del evento:', err);
    }
    setEventForm({
      name: event.name, date: event.date, location: event.location || '',
      discipline: event.discipline || '', category: event.category || '',
      apparatus: event.apparatus || [], selectedLevels: savedLevelIds,
      is_active: event.is_active !== false,
      live_support: event.live_support || false,
      flyer_service: event.flyer_service || false,
      streaming_service: event.streaming_service || false,
      imageFile: null,
      imagePreview: event.image_url || null,
      competition_level: event.competition_level || 'particular',
      judges_config: event.judges_config || {},
      use_trim_mean: event.use_trim_mean !== false
    });
    setActiveSection('create');
  };

  const handleDeleteEvent = async (eventId) => {
    const ok = await confirm({
      title: 'Eliminar Copa',
      message: '¿Estás seguro de eliminar esta copa?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    const result = await eventService.delete(eventId);
    if (!result.error) await loadData();
  };

  // Event code / QR
  const handleShowQR = async (event) => {
    setCodeLoading(event.id);
    try {
      let { data: codeData } = await eventAccessService.getEventCode(event.id);
      if (!codeData) {
        const genResult = await eventAccessService.generateEventCode(event.id);
        codeData = genResult.data;
      }
      if (codeData) setQrModal({ eventId: event.id, eventName: event.name, code: codeData.code });
    } catch (err) {
      console.error('Error loading code:', err);
    } finally {
      setCodeLoading(null);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const pendingRequests = useMemo(() => accessRequests.filter(r => r.status === 'pending'), [accessRequests]);

  // Results / Analytics
  const handleLoadResults = async (eventId) => {
    if (!eventId) { setResultsData(null); return; }
    setResultsLoading(true);
    try {
      const event = myEvents.find(e => e.id === eventId);
      const { data: eventClubs } = await eventClubServiceProxy.getByEvent(eventId);
      const approvedClubs = (eventClubs || []).filter(ec => ec.status === 'approved');
      const clubIds = approvedClubs.map(ec => ec.clubs?.id).filter(Boolean);

      // Cargar gimnasts del club
      let gymnasts = [];
      if (clubIds.length > 0) {
        const { data: gData } = await gymnastServiceProxy.getByIds(clubIds);
        gymnasts = gData || [];
      }

      // Cargar scores de competition_scores (nueva tabla)
      const { data: compScores, error: csError } = await supabase
        .from('competition_scores')
        .select('*, gimnasts(id, name, club_id, level, discipline)')
        .eq('event_id', eventId);

      // Agregar los scores de competition_scores a los gimnasts
      const enrichedGymnasts = gymnasts.map(g => {
        const compScore = (compScores || []).find(cs => cs.gimnasts?.id === g.id || cs.gymnast_id === g.id);
        return {
          ...g,
          // Priorizar scores de competition_scores si existen
          scores: compScore?.scores || g.scores || {},
          total: compScore?.total ?? g.total,
          competitionScoreId: compScore?.id
        };
      });

      setResultsData({ event, clubs: approvedClubs.map(ec => ec.clubs).filter(Boolean), gymnasts: enrichedGymnasts });
    } catch (err) { console.error('Error loading results:', err); }
    finally { setResultsLoading(false); }
  };

  const analytics = useMemo(() => {
    if (!resultsData || !resultsData.gymnasts.length) return null;
    const { gymnasts, clubs } = resultsData;
    const clubStats = clubs.map(club => {
      const cg = gymnasts.filter(g => g.club_id === club.id);
      const scores = cg.flatMap(g => Object.values(g.scores || {}).filter(v => v > 0));
      const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      return { name: club.name, avg: +avg.toFixed(2), athletes: cg.length, totalScores: scores.length };
    }).sort((a, b) => b.avg - a.avg);
    const rankedAthletes = gymnasts.map(g => {
      const sc = Object.values(g.scores || {}).filter(v => v > 0);
      const avg = sc.length > 0 ? sc.reduce((a, b) => a + b, 0) / sc.length : 0;
      const total = sc.reduce((a, b) => a + b, 0);
      return { ...g, avg: +avg.toFixed(2), total: +total.toFixed(2), clubName: clubs.find(c => c.id === g.club_id)?.name || '' };
    }).sort((a, b) => b.total - a.total);
    const appMap = {};
    gymnasts.forEach(g => Object.entries(g.scores || {}).forEach(([app, val]) => {
      if (val > 0) { if (!appMap[app]) appMap[app] = []; appMap[app].push(val); }
    }));
    const apparatusAvg = Object.entries(appMap).map(([app, vals]) => ({
      apparatus: app, avg: +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2), count: vals.length
    }));
    const levelMap = {};
    gymnasts.forEach(g => { const l = g.level || 'Sin nivel'; levelMap[l] = (levelMap[l] || 0) + 1; });
    const levelDist = Object.entries(levelMap).map(([name, value]) => ({ name, value }));
    return { clubStats, rankedAthletes, apparatusAvg, levelDist, totalGymnasts: gymnasts.length, totalClubs: clubs.length };
  }, [resultsData]);

  // ===== PDF Downloads =====
  const handleLoadDownloadsEvent = async (eventId) => {
    setDownloadsEventId(eventId);
    setSelectedScoreApparatus('');
    setSelectedScoreLevel('');
    setSelectedScoreFranja('');
    if (!eventId) {
      setDownloadsEvent(null);
      setDownloadsEventLevels([]);
      setDownloadsClubSummary([]);
      return;
    }
    setDownloadsLoading(true);
    try {
      const event = myEvents.find(e => e.id === eventId);
      setDownloadsEvent(event);
      const [levelsRes, summaryRes] = await Promise.all([
        levelService.getEventLevels(eventId),
        eventGymnastService.getEventSummary(eventId)
      ]);
      setDownloadsEventLevels(levelsRes.data || []);
      setDownloadsClubSummary(summaryRes.data || []);
    } catch (err) { console.error('Error cargando datos para descargas:', err); }
    finally { setDownloadsLoading(false); }
  };

  const handleDownloadResultsPDF = () => {
    if (!resultsData?.event || !analytics) return;
    generateResultsPDF(resultsData.event, analytics);
  };

  const handleDownloadClubsPDF = async (eventId) => {
    const event = myEvents.find(e => e.id === eventId);
    if (!event) return;
    try {
      const eventWithImg = { ...event };
      const { data: eventClubs } = await eventClubServiceProxy.getByEvent(eventId);
      const approvedClubs = (eventClubs || []).filter(ec => ec.status === 'approved');
      const clubs = approvedClubs.map(ec => ec.clubs).filter(Boolean);
      generateClubsPDF(eventWithImg, clubs, approvedClubs, downloadsClubSummary);
    } catch (err) { console.error('Error generando PDF de clubes:', err); }
  };

  const handleDownloadClubsWithAthletesPDF = async (eventId) => {
    const event = myEvents.find(e => e.id === eventId);
    if (!event) return;
    try {
      const summary = downloadsClubSummary.length > 0 ? downloadsClubSummary : (await eventGymnastService.getEventSummary(eventId)).data || [];
      // Load club logos for PDF
      const clubIds = summary.map(cs => cs.club?.id).filter(Boolean);
      const logoMap = {};
      await Promise.all(clubIds.map(async (cid) => {
        try {
          const { data: imgUrl } = await imageServiceProxy.getByEntity('club', cid);
          if (imgUrl) logoMap[cid] = imgUrl;
        } catch (e) { /* ignore */ }
      }));
      generateClubsWithAthletesPDF({ ...event }, summary, logoMap);
    } catch (err) { console.error('Error generando PDF de clubes con atletas:', err); }
  };

  const handleDownloadScoreSheets = async (apparatusCode) => {
    if (!downloadsEvent) return;
    try {
      generateJudgeScoreSheetsPDF(
        downloadsEvent,
        downloadsEventLevels,
        downloadsClubSummary,
        apparatusCode,
        { level: selectedScoreLevel, franja: selectedScoreFranja }
      );
    } catch (err) { console.error('Error generando planillas:', err); }
  };

  const handleApproveRequest = async (id) => {
    try { await eventAccessService.approveRequest(id); setAccessRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r)); }
    catch (err) { console.error('Error:', err); }
  };
  const handleRejectRequest = async (id) => {
    try { await eventAccessService.rejectRequest(id); setAccessRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r)); }
    catch (err) { console.error('Error:', err); }
  };

  // Manage event: load all requests for a specific event
  const handleManageEvent = async (event) => {
    setManagingEvent(event);
    setManagingLoading(true);
    try {
      const { data } = await eventAccessService.getRequestsByEvents([event.id]);
      setManagingRequests(data || []);
    } catch (err) { console.error('Error loading event requests:', err); }
    finally { setManagingLoading(false); }
  };

  const handleManageApprove = async (id) => {
    try {
      await eventAccessService.approveRequest(id);
      setManagingRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
      setAccessRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
    } catch (err) { console.error('Error:', err); }
  };

  const handleManageReject = async (id) => {
    try {
      await eventAccessService.rejectRequest(id);
      setManagingRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
      setAccessRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    } catch (err) { console.error('Error:', err); }
  };

  const handleRemoveClubFromEvent = async (requestId) => {
    const ok = await confirm({
      title: 'Eliminar Club de la Copa',
      message: '¿Estás seguro de eliminar este club de la copa?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    try {
      const { error } = await eventClubServiceProxy.deleteById(requestId);
      if (!error) {
        setManagingRequests(prev => prev.filter(r => r.id !== requestId));
        setAccessRequests(prev => prev.filter(r => r.id !== requestId));
      }
    } catch (err) { console.error('Error removing club:', err); }
  };

  // Solicitudes filtered by event
  const filteredSolicitudes = useMemo(() => {
    if (!solicitudesEventFilter) return [];
    const event = myEvents.find(e => e.id === solicitudesEventFilter);
    const federatedOnly = event?.federated_only;
    let requests = accessRequests.filter(r => r.event_id === solicitudesEventFilter || r.events?.id === solicitudesEventFilter);
    if (federatedOnly) {
      requests = requests.filter(r => r.clubs?.is_federated);
    }
    return requests;
  }, [accessRequests, solicitudesEventFilter, myEvents]);

  // Load club logos when access requests change
  useEffect(() => {
    if (accessRequests.length === 0) return;
    const loadLogos = async () => {
      const clubIds = [...new Set(accessRequests.map(r => r.clubs?.id).filter(Boolean))];
      const toLoad = clubIds.filter(id => !clubLogoMap[id]);
      if (toLoad.length === 0) return;
      const results = await Promise.all(toLoad.map(async (clubId) => {
        try {
          const { data: imgUrl } = await imageServiceProxy.getByEntity('club', clubId);
          if (imgUrl) {
            return [clubId, imgUrl];
          }
        } catch (e) { /* ignore */ }
        return null;
      }));
      const newLogos = {};
      results.filter(Boolean).forEach(([id, url]) => { newLogos[id] = url; });
      if (Object.keys(newLogos).length > 0) {
        setClubLogoMap(prev => ({ ...prev, ...newLogos }));
      }
    };
    loadLogos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessRequests]);

  // Open club detail modal
  const openClubDetail = async (req) => {
    const club = req.clubs;
    if (!club?.id) return;
    setClubDetailLoading(true);
    setClubDetailModal({ club, logo: clubLogoMap[club.id] || null, athleteCount: '...', coachCount: '...' });
    try {
      const [athleteRes, coachRes] = await Promise.all([
        gymnastServiceProxy.count(club.id),
        coachClubAccessServiceProxy.count(club.id, 'approved')
      ]);
      setClubDetailModal(prev => prev ? {
        ...prev,
        athleteCount: athleteRes.count ?? 0,
        coachCount: coachRes.count ?? 0
      } : null);
    } catch (e) {
      console.error('Error loading club detail:', e);
    } finally {
      setClubDetailLoading(false);
    }
  };

  const handleLogout = async () => { await signOut(); navigate('/'); };

  const goTo = (section) => {
    setSectionVisible(false);
    setTimeout(() => {
      setActiveSection(section);
      setSidebarOpen(false);
      if (section === 'create' && !editingEvent) resetEventForm();
      setSectionVisible(true);
    }, 150);
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.15, maxWidthOrHeight: 400, useWebWorker: true });
      setProfilePhotoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePhotoPreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch (err) { alert('Error al procesar la imagen'); }
  };

  const handleSaveProfile = async () => {
    if (!organizer) return;
    setProfileSaving(true);
    try {
      // Update organizer data
      const { error } = await organizerServiceProxy.update(organizer.id, profileData);
      if (error) throw new Error(error);

      // Upload new photo if changed
      if (profilePhotoFile) {
        const fileExt = profilePhotoFile.name.split('.').pop();
        const fileName = `organizers/${organizer.id}-${Date.now()}.${fileExt}`;
        const result = await storageServiceProxy.upload('gymnastics-images', fileName, profilePhotoFile);
        if (result.success && result.data?.publicUrl) {
          const photoUrl = result.data.publicUrl;
          setProfilePhotoUrl(photoUrl);
          setProfilePhotoPreview(null);
          // Guardar en tabla images
          await imageServiceProxy.setImage('organizer', organizer.id, photoUrl);
        }
      }
      alert('Perfil actualizado');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setProfileSaving(false); }
  };

  const handleOrganizerChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (!newPassword) { setPasswordError('La nueva contraseña es obligatoria'); return; }
    if (newPassword.length < 6) { setPasswordError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmNewPassword) { setPasswordError('Las contraseñas no coinciden'); return; }
    setChangingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) throw new Error(error);
      setPasswordSuccess('Contraseña actualizada correctamente');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordForm(false);
      alert('Contraseña actualizada');
    } catch (err) { setPasswordError(err.message || 'Error al cambiar contraseña'); }
    finally { setChangingPassword(false); }
  };

  if (loading && !organizer) {
    return <div className="od-loading"><div className="od-spinner" /><p>Cargando dashboard...</p></div>;
  }

  return (
    <div className="od-layout">
      {/* ========== SIDEBAR ========== */}
      <button className="od-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
      {sidebarOpen && <div className="od-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`od-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="od-sidebar-brand">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="od-sidebar-logo-img" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />

        </div>

        <nav className="od-sidebar-nav">
          {SIDEBAR_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.key;
            const badge = item.key === 'solicitudes' && pendingRequests.length > 0 ? pendingRequests.length : null;
            return (
              <button key={item.key} className={`od-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => goTo(item.key)} style={{ '--item-color': item.color }}>
                <span className="od-nav-icon"><Icon size={20} /></span>
                <span className="od-nav-label">{item.label}</span>
                {badge && <span className="od-nav-badge">{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="od-sidebar-footer">
          <button
            type="button"
            className="od-user-card od-user-card-btn"
            onClick={() => { setActiveSection('profile'); setSidebarOpen(false); }}
            title="Ver mi perfil"
            style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', textAlign: 'left' }}
          >
            <div className="od-user-avatar" style={{ overflow: 'hidden' }}>
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt={organizer?.name || 'Organizador'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              ) : (organizer?.name?.[0]?.toUpperCase() || 'O')}
            </div>
            <div className="od-user-info">
              <p className="od-user-name">{organizer?.name || 'Organizador'}</p>
              <p className="od-user-role">Mi perfil</p>
            </div>
          </button>
          <button className="od-sidebar-logout" onClick={handleLogout}>
            <LogOut size={18} /><span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* ========== MAIN CONTENT ========== */}
      <main className="od-main">
        <ClubTopbar
          pageTitle={SIDEBAR_ITEMS.find(s => s.key === activeSection)?.label || 'Panel'}
          onToggleSidebar={() => setSidebarOpen(o => !o)}
          clubName={organizer?.name || ''}
          clubLogoUrl={profilePhotoUrl || null}
          initials={(organizer?.name?.[0] || 'O').toUpperCase()}
          role="Organizador"
          roleLabel="Cuenta de organizador"
          showSettings={false}
          onGoProfile={() => { setActiveSection('profile'); setSidebarOpen(false); }}
          onLogout={handleLogout}
          notificationsCount={pendingRequests.length}
        />

        <div className="od-content">

          {/* ========================= PROFILE SECTION ========================= */}
          {activeSection === 'profile' && (
            <>
              <p className="od-page-subtitle"><User size={16} />Administra tus datos, contraseña y foto de perfil como organizador.</p>
            <div className="od-card">
              <div className="od-card-header">
                <h2><User size={20} className="icon-purple" /> Datos personales</h2>
              </div>

              <div className="od-profile-section" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {/* Photo */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <label style={{ cursor: 'pointer', position: 'relative' }}>
                    {(profilePhotoPreview || profilePhotoUrl) ? (
                      <img src={profilePhotoPreview || profilePhotoUrl} alt="Foto" style={{ width: 100, height: 100, borderRadius: 12, objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 100, height: 100, borderRadius: 12, background: '#f4f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        <Camera size={32} />
                      </div>
                    )}
                    <div style={{ position: 'absolute', bottom: 4, right: 4, background: '#8b5cf6', borderRadius: '50%', padding: 6, color: 'white' }}>
                      <Camera size={14} />
                    </div>
                    <input type="file" accept="image/*" onChange={handleProfilePhotoChange} style={{ display: 'none' }} />
                  </label>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>JPG, PNG — máx. 150KB</span>
                </div>

                {/* Form */}
                <div style={{ flex: 1, minWidth: 250 }}>
                  <div className="od-form-grid" style={{ marginTop: 0 }}>
                    <div className="od-form-group">
                      <label>Nombre</label>
                      <input type="text" value={profileData.name} onChange={e => setProfileData({ ...profileData, name: e.target.value })} placeholder="Tu nombre" />
                    </div>
                    <div className="od-form-group">
                      <label>Organización</label>
                      <input type="text" value={profileData.organization} onChange={e => setProfileData({ ...profileData, organization: e.target.value })} placeholder="Nombre de tu organización" />
                    </div>
                    <div className="od-form-group">
                      <label>Teléfono</label>
                      <input type="tel" value={profileData.phone} onChange={e => setProfileData({ ...profileData, phone: e.target.value })} placeholder="Teléfono de contacto" />
                    </div>
                    <div className="od-form-group">
                      <label>Ubicación</label>
                      <input type="text" value={profileData.location} onChange={e => setProfileData({ ...profileData, location: e.target.value })} placeholder="Ciudad, País" />
</div>
                  </div>
                  <button onClick={handleSaveProfile} disabled={profileSaving} className="od-btn od-btn-primary" style={{ marginTop: 12 }}>
                    {profileSaving ? 'Guardando...' : 'Guardar Cambios'}
                  </button>

                  <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e4e4e7' }}>
                    {!showPasswordForm ? (
                      <button className="od-btn od-btn-secondary" onClick={() => setShowPasswordForm(true)}>
                        Cambiar Contraseña
                      </button>
                    ) : (
                      <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>Nueva Contraseña</h4>
                        <div className="od-form-group">
                          <label>Nueva Contraseña</label>
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
                        </div>
                        <div className="od-form-group">
                          <label>Confirmar Contraseña</label>
                          <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="Repite la contraseña" />
                        </div>
                        {passwordError && <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '8px 0' }}>{passwordError}</p>}
                        {passwordSuccess && <p style={{ color: '#059669', fontSize: '0.85rem', margin: '8px 0' }}>{passwordSuccess}</p>}
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button className="od-btn od-btn-primary" onClick={handleOrganizerChangePassword} disabled={changingPassword}>
                            {changingPassword ? 'Cambiando...' : 'Confirmar'}
                          </button>
                          <button className="od-btn od-btn-secondary" onClick={() => { setShowPasswordForm(false); setNewPassword(''); setConfirmNewPassword(''); setPasswordError(''); }}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </>
          )}

          {/* ========================= EVENTS LIST ========================= */}
          {activeSection === 'events' && (
            <>
              <p className="od-page-subtitle"><Calendar size={16} />Gestiona tus copas: créalas, edítalas, genera códigos QR y controla inscripciones.</p>
            <div className="od-card">
              <div className="od-card-header">
                <h2><Calendar size={20} className="icon-blue" /> Listado de copas</h2>
                <button onClick={() => goTo('create')} className="od-btn od-btn-success">
                  <Plus size={18} /> Nueva Copa
                </button>
              </div>
              <div className="od-month-nav">
                <button onClick={handlePrevMonth} className="od-month-btn"><ChevronLeft size={20} /></button>
                <span className="od-month-label">{MONTH_NAMES[selectedMonth]} {selectedYear}</span>
                <button onClick={handleNextMonth} className="od-month-btn"><ChevronRight size={20} /></button>
                {(selectedMonth !== today.getMonth() || selectedYear !== today.getFullYear()) && (
                  <button onClick={handleGoToCurrentMonth} className="od-month-today">Hoy</button>
                )}
                <span className="od-month-count">{filteredEvents.length} copa{filteredEvents.length !== 1 ? 's' : ''}</span>
              </div>

              {loading ? (
                <div className="od-empty"><div className="od-spinner" /><p>Cargando copas...</p></div>
              ) : filteredEvents.length === 0 ? (
                <div className="od-empty">
                  <Calendar size={48} strokeWidth={1} className="icon-blue" style={{ opacity: 0.4 }} />
                  <p>No hay copas en {MONTH_NAMES[selectedMonth]} {selectedYear}</p>
                </div>
              ) : (
                <div className="od-events-table">
                  <table>
                    <thead><tr><th>Copa</th><th>Fecha</th><th>Ubicación</th><th>Estado</th><th>Servicios</th><th style={{ textAlign: 'center' }}>Acciones</th></tr></thead>
                    <tbody>
                      {filteredEvents.map(event => {
                        const approvalStatus = event.approval_status || 'approved';
                        return (
                        <tr key={event.id} style={{ opacity: approvalStatus === 'rejected' ? 0.5 : 1 }}>
                          <td className="od-event-name">
                            {event.image_url ? <img src={event.image_url} alt="" className="od-event-thumb" /> : <span className="od-event-thumb-placeholder">🏆</span>}
                            <div>
                              {event.name}
                              {event.slug && event.approval_status === 'approved' && (
                                <a
                                  href={`/resultados/${event.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="od-event-public-link"
                                  onClick={e => e.stopPropagation()}
                                  title="Ver página pública"
                                >
                                  🔗 copagimnasta.com/resultados/{event.slug}
                                </a>
                              )}
                            </div>
                          </td>
                          <td>{event.date}</td>
                          <td>{event.location || '—'}</td>
                          <td>
                            {approvalStatus === 'pending_approval' && (
                              <span className="od-badge od-badge-warning"><Clock size={12} /> Pendiente</span>
                            )}
                            {approvalStatus === 'approved' && (
                              <span className={`od-badge ${event.is_active ? 'od-badge-active' : 'od-badge-inactive'}`}>
                                {event.is_active ? '✓ Aprobado' : 'Inactivo'}
                              </span>
                            )}
                            {approvalStatus === 'rejected' && (
                              <span className="od-badge od-badge-danger">✗ Rechazado</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {event.live_support && <span className="od-micro-badge od-micro-cyan" title="Asistencia en Vivo"><Headphones size={11} /></span>}
                              {event.flyer_service && <span className="od-micro-badge od-micro-pink" title="Flyer + QR"><Image size={11} /></span>}
                              {event.streaming_service && <span className="od-micro-badge od-micro-red" title="Transmisión en Vivo"><Video size={11} /></span>}
                              {!event.live_support && !event.flyer_service && !event.streaming_service && <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>}
                            </div>
                          </td>
                          <td>
                            <div className="od-actions">
                              {approvalStatus === 'approved' && (
                                <>
                                  <button onClick={() => handleManageEvent(event)} className="od-action-btn od-action-green" title="Administrar">
                                    <Settings size={16} />
                                  </button>
                                  <button onClick={() => { setViewingEventAthletes(event); setActiveSection('event-athletes'); }} className="od-action-btn" style={{ background: '#f0fdfa', color: '#14b8a6', border: '1px solid #99f6e4' }} title="Ver Atletas Inscritas">
                                    <Award size={16} />
                                  </button>
                                  <button onClick={() => handleShowQR(event)} className="od-action-btn od-action-purple" title="Código QR">
                                    {codeLoading === event.id ? <span className="od-btn-spinner" /> : <QrCode size={16} />}
                                  </button>
                                </>
                              )}
                              <button onClick={() => handleEditEvent(event)} className="od-action-btn od-action-blue" title="Editar"><Edit2 size={16} /></button>
                              <button onClick={() => handleDeleteEvent(event.id)} className="od-action-btn od-action-danger" title="Eliminar"><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>
          )}

          {/* ========================= CREATE / EDIT EVENT ========================= */}
          {activeSection === 'create' && (
            <>
              <p className="od-page-subtitle"><Plus size={16} />{editingEvent ? 'Modifica los datos de tu copa existente.' : 'Completa los datos para crear una nueva copa y enviarla para aprobación.'}</p>
            <div className="od-card">
              <div className="od-card-header">
                <h2><Plus size={20} className="icon-green" /> {editingEvent ? 'Editar Copa' : 'Nueva Copa'}</h2>
              </div>
              <div className="od-card-body">
                {formError && <div className="od-alert od-alert-error"><AlertCircle size={16} /> {formError}</div>}

                <div className="od-form-section">
                  <div className="od-form-group">
                    <label>Nombre de la Copa *</label>
                    <input type="text" value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                      placeholder="Copa Nacional de Gimnasia 2026" className="od-input" />
                  </div>

                  <div className="od-form-group">
                    <label>Categoría Rápida</label>
                    <div className="od-category-tabs">
                      {CATEGORY_TABS.map(tab => {
                        const isActive = eventForm.discipline === tab.discipline && (tab.category === '' || eventForm.category === tab.category);
                        return (
                          <button key={tab.key} type="button" className={`od-category-tab ${isActive ? 'active' : ''}`}
                            onClick={() => setEventForm(prev => ({ ...prev, discipline: tab.discipline, category: tab.category, apparatus: [] }))}>
                            <img src={tab.logo} alt={tab.name} />
                            <span className="od-cat-name">{tab.name}</span>
                            <span className="od-cat-full">{tab.fullName}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <DisciplineSelector selectedDiscipline={eventForm.discipline || ''} selectedCategory={eventForm.category || ''}
                    onDisciplineChange={(d) => setEventForm(prev => ({ ...prev, discipline: d, category: '', apparatus: [] }))}
                    onCategoryChange={(c) => setEventForm(prev => ({ ...prev, category: c, apparatus: [] }))} />

                  <ApparatusSelector discipline={eventForm.discipline} category={eventForm.category}
                    selectedApparatus={eventForm.apparatus || []}
                    onApparatusChange={(a) => setEventForm({ ...eventForm, apparatus: a })} allowMultiple={true} />

                  <div className="od-form-group">
                    <label>Niveles de la Copa</label>
                    <LevelSelectorSimple selectedLevelIds={eventForm.selectedLevels || []}
                      onChange={(levels) => setEventForm({ ...eventForm, selectedLevels: levels })} />
                  </div>

                  <div className="od-form-row">
                    <div className="od-form-group">
                      <label>Fecha *</label>
                      <input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="od-input" />
                    </div>
                    <div className="od-form-group">
                      <label>Ubicación</label>
                      <input type="text" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                        placeholder="Ciudad, País" className="od-input" />
                    </div>
                  </div>

                  <div className="od-form-group">
                    <label>Imagen de la Copa (opcional)</label>
                    <div className="od-image-upload">
                      <label className="od-btn od-btn-outline">
                        <ImageIcon size={16} /> {eventForm.imagePreview ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
                        <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                      </label>
                      {eventForm.imagePreview && <button onClick={clearImagePreview} className="od-btn od-btn-ghost od-btn-sm"><X size={14} /> Quitar</button>}
                    </div>
                    {eventForm.imagePreview && <div className="od-image-preview"><img src={eventForm.imagePreview} alt="Preview" /></div>}
                  </div>

                  <div className="od-form-group">
                    <label>Reglas de la Copa / PDF de Convocatoria (opcional)</label>
                    <div className="od-image-upload">
                      <label className="od-btn od-btn-outline">
                        <FileText size={16} /> {eventForm.rulesPdfPreview ? 'Cambiar PDF' : 'Subir PDF de Reglas'}
                        <input type="file" accept="application/pdf" onChange={handleRulesPdfChange} style={{ display: 'none' }} />
                      </label>
                      {eventForm.rulesPdfPreview && <button onClick={clearRulesPdfPreview} className="od-btn od-btn-ghost od-btn-sm"><X size={14} /> Quitar</button>}
                    </div>
                    {eventForm.rulesPdfPreview && <div className="od-image-preview"><embed src={eventForm.rulesPdfPreview} type="application/pdf" style={{ height: '150px', width: '100%' }} /></div>}
                    <small style={{ color: '#6b7280', marginTop: 4, display: 'block' }}>Este documento será visible para los clubes invitados al evento.</small>
                  </div>

                  {/* === Nivel de Copa y Configuración de Jueces === */}
                  <div className="od-form-group">
                    <label>Nivel / Tipo de Copa</label>
                    <select
                      value={eventForm.competition_level || 'particular'}
                      onChange={(e) => {
                        const level = e.target.value;
                        const recommended = JUDGES_CONFIG_BY_LEVEL[level] || {};
                        setEventForm(prev => ({
                          ...prev,
                          competition_level: level,
                          judges_config: recommended,
                          use_trim_mean: recommended.trim !== false
                        }));
                      }}
                      className="od-input"
                    >
                      {COMPETITION_LEVELS.map(cl => (
                        <option key={cl.code} value={cl.code}>{cl.label} — {cl.description}</option>
                      ))}
                    </select>
                  </div>

                  <div className="od-form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={eventForm.federated_only} onChange={e => setEventForm({ ...eventForm, federated_only: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#8b5cf6' }} />
                      Solo Clubes Federados
                    </label>
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 26 }}>Solo clubes federados podrán participar</span>
                  </div>

                  {eventForm.competition_level && JUDGES_CONFIG_BY_LEVEL[eventForm.competition_level] && (
                    <div className="od-judges-config-preview">
                      <label className="od-form-label-section">⚖️ Jueces sugeridos según FIG 2024-2028</label>
                      <p className="od-judges-config-hint">Referencia informativa basada en el nivel <strong>{COMPETITION_LEVELS.find(c => c.code === eventForm.competition_level)?.label}</strong>. Puedes enviar la cantidad de códigos de invitación que necesites. Cada juez elegirá su rol (D, E, A, etc.) al configurar su panel.</p>
                      <div className="od-judges-panels-grid">
                        {[
                          { key: 'D', label: 'Panel D (Dificultad)', color: '#7c3aed' },
                          { key: 'E', label: 'Panel E (Ejecución)', color: '#2563eb' },
                          { key: 'A', label: 'Panel A (Artístico)', color: '#db2777' },
                          { key: 'L', label: 'Juez de Línea', color: '#059669' },
                          { key: 'T', label: 'Juez de Tiempo', color: '#d97706' },
                          { key: 'R', label: 'Juez Superior', color: '#64748b' },
                        ].map(panel => {
                          const recommended = JUDGES_CONFIG_BY_LEVEL[eventForm.competition_level] || {};
                          const count = recommended[panel.key] ?? 0;
                          if (count === 0) return null;
                          return (
                            <div key={panel.key} className="od-judge-panel-item">
                              <span className="od-judge-panel-dot" style={{ background: panel.color }}>{panel.key}</span>
                              <span className="od-judge-panel-label">{panel.label}</span>
                              <span className="od-judge-panel-count">{count} sugeridos</span>
                            </div>
                          );
                        }).filter(Boolean)}
                      </div>
                      {JUDGES_CONFIG_BY_LEVEL[eventForm.competition_level]?.trim && (
                        <div className="od-trim-hint">
                          <AlertCircle size={14} />
                          <span>Para este nivel se recomienda <strong>eliminación de extremos</strong> (quitar nota más alta y más baja del Panel E antes de promediar).</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* === Servicios Adicionales === */}
                  <div className="od-services-section">
                    <label className="od-form-label-section">Servicios Adicionales</label>

                    <div className="od-service-card" onClick={() => setEventForm(prev => ({ ...prev, live_support: !prev.live_support }))}>
                      <div className={`od-service-check ${eventForm.live_support ? 'active' : ''}`}>
                        {eventForm.live_support ? <CheckCircle size={18} /> : <div className="od-service-empty-check" />}
                      </div>
                      <div className="od-service-body">
                        <div className="od-service-title"><Headphones size={16} /> Asistencia en Vivo</div>
                        <p className="od-service-desc">Apoyo técnico-logístico el día de la copa: inscripciones de atletas, modificaciones y cambios de información de última hora desde un panel administrador.</p>
                        <span className="od-service-price">💰 Valor adicional</span>
                      </div>
                    </div>

                    <div className="od-service-card" onClick={() => setEventForm(prev => ({ ...prev, flyer_service: !prev.flyer_service }))}>
                      <div className={`od-service-check ${eventForm.flyer_service ? 'active' : ''}`}>
                        {eventForm.flyer_service ? <CheckCircle size={18} /> : <div className="od-service-empty-check" />}
                      </div>
                      <div className="od-service-body">
                        <div className="od-service-title"><Image size={16} /> Generar Flyer para la Copa</div>
                        <p className="od-service-desc">Creación de bocetos representativos de la copa. Entrega de afiches con código QR para que el público escanee y vea los resultados en vivo desde su celular.</p>
                        <span className="od-service-price">💰 Valor adicional</span>
                      </div>
                    </div>

                    <div className="od-service-card" onClick={() => setEventForm(prev => ({ ...prev, streaming_service: !prev.streaming_service }))}>
                      <div className={`od-service-check ${eventForm.streaming_service ? 'active' : ''}`}>
                        {eventForm.streaming_service ? <CheckCircle size={18} /> : <div className="od-service-empty-check" />}
                      </div>
                      <div className="od-service-body">
                        <div className="od-service-title"><Video size={16} /> Transmisión del Evento en Vivo</div>
                        <p className="od-service-desc">Transmisión profesional de la copa por YouTube y/o Twitch. Incluye configuración de streaming, gráficos de resultados en pantalla y enlace compartible para familiares y público.</p>
                        <span className="od-service-price">💰 Valor adicional</span>
                      </div>
                    </div>
                  </div>

                  {/* Approval notice */}
                  {!editingEvent && (
                    <div className="od-approval-notice">
                      <ShieldCheck size={18} />
                      <div>
                        <strong>Flujo de aprobación</strong>
                        <p>Al crear la copa, será enviada para revisión y aprobación por parte de la administración de CopaGimnasta. Recibirás una notificación cuando sea aprobada. Una vez aprobada, la copa será visible para clubes y público general.</p>
                      </div>
                    </div>
                  )}

                  <div className="od-info-notice">
                    <AlertCircle size={16} />
                    <p>Los clubes y jueces se agregarán mediante códigos de invitación desde las secciones correspondientes del menú lateral, una vez la copa esté aprobada.</p>
                  </div>

                  <div className="od-form-actions">
                    <button onClick={() => { resetEventForm(); setActiveSection('events'); }} className="od-btn od-btn-ghost">Cancelar</button>
                    <button onClick={handleCreateEvent} disabled={formLoading || uploadingImage} className="od-btn od-btn-success" style={{ padding: '9px 20px' }}>
                      {formLoading || uploadingImage ? <><span className="od-btn-spinner" /> Guardando...</> : <><Save size={16} /> {editingEvent ? 'Actualizar' : 'Enviar para Aprobación'}</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}

          {/* ========================= CODES / QR ========================= */}
          {activeSection === 'codes' && (
            <>
              <p className="od-page-subtitle"><QrCode size={16} />Genera códigos QR y enlaces públicos para que clubes y jueces accedan a tus copas.</p>
            <div className="od-card">
              <div className="od-card-header">
                <h2><QrCode size={20} className="icon-purple" /> Códigos y enlaces</h2>
              </div>
              <div className="od-card-body">
                <p className="od-desc">Genera códigos de acceso para tus copas. Los clubes podrán escanear el QR o ingresar el código para solicitar participar.</p>
                {myEvents.filter(e => e.is_active).length === 0 ? (
                  <div className="od-empty">
                    <QrCode size={48} strokeWidth={1} className="icon-purple" style={{ opacity: 0.4 }} />
                    <p>No tienes copas activas</p>
                  </div>
                ) : (
                  <div className="od-codes-grid">
                    {myEvents.filter(e => e.is_active).map(event => (
                      <div key={event.id} className="od-code-card">
                        <div className="od-code-card-top">
                          <QrCode size={20} className="icon-purple" />
                          <h4>{event.name}</h4>
                        </div>
                        <p className="od-code-date">{event.date} • {event.location || 'Sin ubicación'}</p>
                        <button onClick={() => handleShowQR(event)} className="od-btn od-btn-purple od-btn-sm" disabled={codeLoading === event.id}>
                          {codeLoading === event.id ? <span className="od-btn-spinner" /> : <><QrCode size={16} /> Ver Código / QR</>}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* ========================= INVITACIONES (UNIFIED) ========================= */}
          {activeSection === 'invitations' && (
            <>
              <p className="od-page-subtitle"><Send size={16} />Envía invitaciones a jueces y clubes para tus copas.</p>
            <div className="od-card od-section-enter">
              <div className="od-card-header">
                <h2><Send size={20} className="icon-pink" /> Envío de invitaciones</h2>
              </div>
              <div className="od-card-body">
                <div className="od-inv-tabs">
                  <button
                    className={`od-inv-tab ${invitationTab === 'judges' ? 'active' : ''}`}
                    onClick={() => setInvitationTab('judges')}
                  >
                    <ClipboardList size={16} /> Jueces
                  </button>
                  <button
                    className={`od-inv-tab ${invitationTab === 'clubs' ? 'active' : ''}`}
                    onClick={() => setInvitationTab('clubs')}
                  >
                    <Building2 size={16} /> Clubes
                  </button>
                </div>
                <div className="od-inv-content">
                  {invitationTab === 'judges' && (
                    <JudgeInvitationManager events={myEvents} judges={judges} auth={organizer} />
                  )}
                  {invitationTab === 'clubs' && (
                    <ClubInvitationManager events={myEvents} auth={organizer} />
                  )}
                </div>
              </div>
            </div>
            </>
          )}

          {/* ========================= SOLICITUDES ========================= */}
          {activeSection === 'solicitudes' && (
            <>
              <p className="od-page-subtitle"><Bell size={16} />Revisa y aprueba las solicitudes de clubes que piden acceso a tus copas.</p>
            <div className="od-card">
              <div className="od-card-header">
                <h2><Bell size={20} className="icon-amber" /> Solicitudes pendientes</h2>
                <button onClick={loadData} className="od-btn od-btn-ghost"><RefreshCw size={16} /> Actualizar</button>
              </div>
              <div className="od-card-body">
                <p className="od-desc">Selecciona una copa para ver las solicitudes de los clubes.</p>

                {/* Event Filter */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Copa:</label>
                  <select value={solicitudesEventFilter} onChange={(e) => setSolicitudesEventFilter(e.target.value)} className="od-input" style={{ maxWidth: 420 }}>
                    <option value="">— Selecciona una copa —</option>
                    {myEvents.map(ev => <option key={ev.id} value={ev.id}>{ev.name} — {ev.date}</option>)}
                  </select>
                </div>

                {!solicitudesEventFilter && (
                  <div className="od-empty">
                    <ClipboardList size={48} strokeWidth={1} className="icon-amber" style={{ opacity: 0.4 }} />
                    <p>Selecciona una copa para ver sus solicitudes</p>
                    <span className="od-empty-hint">{accessRequests.filter(r => r.status === 'pending').length > 0
                      ? `Tienes ${accessRequests.filter(r => r.status === 'pending').length} solicitud${accessRequests.filter(r => r.status === 'pending').length > 1 ? 'es' : ''} pendiente${accessRequests.filter(r => r.status === 'pending').length > 1 ? 's' : ''} en total`
                      : 'Comparte el código de tus copas para recibir solicitudes'}</span>
                  </div>
                )}

                {solicitudesEventFilter && filteredSolicitudes.filter(r => r.status === 'pending').length > 0 && (
                  <div className="od-requests-section">
                    <h4 className="od-requests-title pending">⏳ Pendientes ({filteredSolicitudes.filter(r => r.status === 'pending').length})</h4>
                    <div className="od-requests-list">
                      {filteredSolicitudes.filter(r => r.status === 'pending').map(req => (
                        <div key={req.id} className="od-request-card od-request-pending">
                          <div className="od-request-info">
                            <div style={{
                              width: 42, height: 42, borderRadius: 10, background: 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', border: '1.5px solid #e4e4e7', flexShrink: 0
                            }}>
                              {clubLogoMap[req.clubs?.id]
                                ? <img src={clubLogoMap[req.clubs.id]} alt={req.clubs?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <Building2 size={20} style={{ color: '#a1a1aa' }} />}
                            </div>
                            <div>
                              <h4>{req.clubs?.name || 'Club'}</h4>
                              <p>{req.clubs?.location || ''}</p>
                              {req.clubs?.is_federated && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.65rem', color: '#8b5cf6', fontWeight: 600, background: '#f5f3ff', padding: '2px 6px', borderRadius: 4, marginTop: 4 }}>
                                🏅 Federado
                              </span>
                              )}
                            </div>
                          </div>
                          <div className="od-request-actions">
                            <button onClick={() => openClubDetail(req)} className="od-btn od-btn-ghost od-btn-sm" title="Ver ficha del club"><Building2 size={14} /> Ver Club</button>
                            <button onClick={() => handleApproveRequest(req.id)} className="od-btn od-btn-approve"><CheckSquare size={16} /> Aprobar</button>
                            <button onClick={() => handleRejectRequest(req.id)} className="od-btn od-btn-reject"><XSquare size={16} /> Rechazar</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approved */}
                {solicitudesEventFilter && filteredSolicitudes.filter(r => r.status === 'approved').length > 0 && (
                  <div className="od-requests-section">
                    <h4 className="od-requests-title">✅ Aprobados ({filteredSolicitudes.filter(r => r.status === 'approved').length})</h4>
                    <div className="od-requests-list">
                      {filteredSolicitudes.filter(r => r.status === 'approved').map(req => (
                        <div key={req.id} className="od-request-card od-request-approved">
                          <div className="od-request-info">
                            <div style={{
                              width: 42, height: 42, borderRadius: 10, background: 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', border: '1.5px solid #d1fae5', flexShrink: 0
                            }}>
                              {clubLogoMap[req.clubs?.id]
                                ? <img src={clubLogoMap[req.clubs.id]} alt={req.clubs?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <Building2 size={20} style={{ color: '#10b981' }} />}
                            </div>
                            <div>
                              <h4>{req.clubs?.name || 'Club'}</h4>
                              <span className="od-badge od-badge-violet">Aprobado</span>
                            </div>
                          </div>
                          <div className="od-request-actions">
                            <button onClick={() => openClubDetail(req)} className="od-btn od-btn-ghost od-btn-sm" title="Ver ficha del club"><Building2 size={14} /> Ver Club</button>
                            <button onClick={() => handleRemoveClubFromEvent(req.id)} className="od-btn od-btn-reject od-btn-sm" title="Eliminar club de la copa"><UserMinus size={14} /> Eliminar</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejected */}
                {solicitudesEventFilter && filteredSolicitudes.filter(r => r.status === 'rejected').length > 0 && (
                  <div className="od-requests-section">
                    <h4 className="od-requests-title">❌ Rechazados ({filteredSolicitudes.filter(r => r.status === 'rejected').length})</h4>
                    <div className="od-requests-list">
                      {filteredSolicitudes.filter(r => r.status === 'rejected').map(req => (
                        <div key={req.id} className="od-request-card od-request-rejected">
                          <div className="od-request-info">
                            <div style={{
                              width: 42, height: 42, borderRadius: 10, background: 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', border: '1.5px solid #fecaca', flexShrink: 0
                            }}>
                              {clubLogoMap[req.clubs?.id]
                                ? <img src={clubLogoMap[req.clubs.id]} alt={req.clubs?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <Building2 size={20} style={{ color: '#ef4444' }} />}
                            </div>
                            <div>
                              <h4>{req.clubs?.name || 'Club'}</h4>
                              <span className="od-badge od-badge-inactive">Rechazado</span>
                            </div>
                          </div>
                          <button onClick={() => openClubDetail(req)} className="od-btn od-btn-ghost od-btn-sm" title="Ver ficha del club"><Building2 size={14} /> Ver Club</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {solicitudesEventFilter && filteredSolicitudes.length === 0 && (
                  <div className="od-empty">
                    <Bell size={48} strokeWidth={1} className="icon-amber" style={{ opacity: 0.4 }} />
                    <p>No hay solicitudes para esta copa</p>
                    <span className="od-empty-hint">Comparte el código QR de tus copas para que los clubes soliciten acceso</span>
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* ========================= CLUB DETAIL MODAL ========================= */}
          {clubDetailModal && (
            <div className="od-modal-overlay" onClick={() => setClubDetailModal(null)}>
              <div className="od-modal od-club-detail-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440, width: '100%' }}>
                <div className="od-modal-header">
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>Ficha del Club</h3>
                  <button onClick={() => setClubDetailModal(null)} className="od-modal-close"><X size={18} /></button>
                </div>
                <div className="od-modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                  {/* Logo */}
                  <div style={{
                    width: 80, height: 80, borderRadius: 16, background: '#f4f4f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', border: '2px solid #e4e4e7'
                  }}>
                    {clubDetailModal.logo
                      ? <img src={clubDetailModal.logo} alt={clubDetailModal.club?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <Building2 size={36} style={{ color: '#a1a1aa' }} />}
                  </div>

                  {/* Name */}
                  <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#18181b', textAlign: 'center' }}>{clubDetailModal.club?.name || 'Club'}</h2>

                  {/* Location */}
                  {clubDetailModal.club?.location && (
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#71717a' }}>📍 {clubDetailModal.club.location}</p>
                  )}

                  {/* Stats */}
                  <div style={{ display: 'flex', gap: 24, marginTop: 4 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#7c3aed' }}>{clubDetailModal.athleteCount}</div>
                      <div style={{ fontSize: '0.75rem', color: '#71717a' }}>Atletas</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#06b6d4' }}>{clubDetailModal.coachCount}</div>
                      <div style={{ fontSize: '0.75rem', color: '#71717a' }}>Entrenadores</div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div style={{ width: '100%', background: '#fafafa', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    {clubDetailModal.club?.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#374151' }}>
                        <span style={{ fontSize: '0.9rem' }}>✉️</span>
                        <a href={`mailto:${clubDetailModal.club.email}`} style={{ color: '#7c3aed', textDecoration: 'none' }}>{clubDetailModal.club.email}</a>
                      </div>
                    )}
                    {clubDetailModal.club?.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#374151' }}>
                        <span style={{ fontSize: '0.9rem' }}>📞</span>
                        <a href={`tel:${clubDetailModal.club.phone}`} style={{ color: '#374151', textDecoration: 'none' }}>{clubDetailModal.club.phone}</a>
                      </div>
                    )}
                  </div>

                  {/* Social Links */}
                  {(clubDetailModal.club?.facebook_url || clubDetailModal.club?.instagram_url || clubDetailModal.club?.twitter_url) && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                      {clubDetailModal.club?.facebook_url && (
                        <a href={clubDetailModal.club.facebook_url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'rgba(24,119,242,0.08)', color: '#1877f2', textDecoration: 'none' }}>
                          <svg width="16" height="16" viewBox="0 0 512 512" fill="currentColor"><path d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V330.1h-58.5v-74h58.5v-56.4c0-57.7 34.4-89.6 87-89.6 25.2 0 51.5 4.5 51.5 4.5v56.6h-29c-28.5 0-37.4 17.7-37.4 35.8V256h63.9l-10.2 74h-53.7v174.4C429.3 476.8 512 376 512 256z"/></svg>
                        </a>
                      )}
                      {clubDetailModal.club?.instagram_url && (
                        <a href={clubDetailModal.club.instagram_url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'rgba(228,64,95,0.08)', color: '#e4405f', textDecoration: 'none' }}>
                          <svg width="16" height="16" viewBox="0 0 448 512" fill="currentColor"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9S160.5 370.9 224.1 370.9 339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1S3.8 127.9 2 163.8c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c 19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>
                        </a>
                      )}
                    </div>
                  )}

                  {/* No contact info */}
                  {!clubDetailModal.club?.email && !clubDetailModal.club?.phone && !clubDetailModal.club?.facebook_url && !clubDetailModal.club?.instagram_url && !clubDetailModal.club?.twitter_url && (
                    <p style={{ fontSize: '0.82rem', color: '#a1a1aa', fontStyle: 'italic', margin: 0 }}>Sin información de contacto</p>
                  )}
                </div>
              </div>
            </div>
          )}


          {/* ========================= EVENT ATHLETES ========================= */}
          {activeSection === 'event-athletes' && (
            <>
              <p className="od-page-subtitle"><Award size={16} />Consulta las atletas inscritas en cada una de tus copas.</p>
            <div className="od-card">
              <div className="od-card-header"><h2><Award size={20} style={{ color: '#14b8a6' }} /> Inscripciones por copa</h2></div>
              <div className="od-card-body">
                {viewingEventAthletes ? (
                  <OrganizerEventAthletes
                    event={viewingEventAthletes}
                    onBack={() => setViewingEventAthletes(null)}
                  />
                ) : (
                  <>
                    <p className="od-desc">Selecciona una copa para ver las atletas inscritas por cada club.</p>
                    <div className="od-results-selector" style={{ marginBottom: 16 }}>
                      <label>Seleccionar copa:</label>
                      <select
                        value={athletesEventId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setAthletesEventId(id);
                          if (id) {
                            const ev = myEvents.find(evt => evt.id === id);
                            if (ev) setViewingEventAthletes(ev);
                          } else {
                            setViewingEventAthletes(null);
                          }
                        }}
                        className="od-input"
                      >
                        <option value="">-- Elige una copa --</option>
                        {myEvents.filter(ev => ev.approval_status === 'approved' || !ev.approval_status).map(ev => (
                          <option key={ev.id} value={ev.id}>{ev.name} — {ev.date}</option>
                        ))}
                      </select>
                    </div>
                    {!athletesEventId && (
                      <div className="od-empty">
                        <Award size={48} strokeWidth={1} style={{ opacity: 0.4, color: '#14b8a6' }} />
                        <p>Selecciona una copa para ver las atletas</p>
                        <span className="od-empty-hint">Los clubes inscriben sus atletas desde su panel una vez aprobada su participación</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            </>
          )}

          {/* ========================= DESCARGABLES ========================= */}
          {activeSection === 'downloads' && (
            <>
              <p className="od-page-subtitle"><Download size={16} />Descarga PDFs de resultados, inscripciones y planillas de jueces.</p>
            <div className="od-card">
              <div className="od-card-header"><h2><Download size={20} style={{ color: '#e55d36' }} /> Reportes disponibles</h2></div>
              <div className="od-card-body">
                <p className="od-desc">Selecciona una copa para generar documentos PDF descargables.</p>
                <div className="od-results-selector">
                  <label>Seleccionar copa:</label>
                  <select value={downloadsEventId} onChange={(e) => handleLoadDownloadsEvent(e.target.value)} className="od-input">
                    <option value="">-- Elige una copa --</option>
                    {myEvents.map(ev => <option key={ev.id} value={ev.id}>{ev.name} — {ev.date}</option>)}
                  </select>
                </div>

                {downloadsLoading && <div className="od-empty"><div className="od-spinner" /><p>Cargando datos de la copa...</p></div>}

                {!downloadsEventId && !downloadsLoading && (
                  <div className="od-empty">
                    <Download size={48} strokeWidth={1} style={{ opacity: 0.4, color: '#e55d36' }} />
                    <p>Selecciona una copa</p>
                  </div>
                )}

                {downloadsEvent && !downloadsLoading && (
                  <div className="od-dl-grid">
                    {/* Clubes participantes */}
                    <div className="od-dl-card">
                      <div className="od-dl-card-icon" style={{ '--dl-color': '#06b6d4' }}><Building2 size={24} /></div>
                      <div className="od-dl-card-info">
                        <h4>Clubes Participantes</h4>
                        <p>Listado de clubes aprobados con email y ubicación.</p>
                      </div>
                      <button className="od-btn od-btn-download" onClick={() => handleDownloadClubsPDF(downloadsEventId)}>
                        <Download size={14} /> Descargar PDF
                      </button>
                    </div>

                    {/* Clubes + Atletas */}
                    <div className="od-dl-card">
                      <div className="od-dl-card-icon" style={{ '--dl-color': '#10b981' }}><Users size={24} /></div>
                      <div className="od-dl-card-info">
                        <h4>Clubes + Atletas Inscritas</h4>
                        <p>Cada club con su listado de atletas (nombre, nivel, franja, edad).</p>
                        {downloadsClubSummary.length > 0 && (
                          <span className="od-dl-badge">{downloadsClubSummary.length} clubes — {downloadsClubSummary.reduce((s, c) => s + c.count, 0)} atletas</span>
                        )}
                      </div>
                      <button className="od-btn od-btn-download" onClick={() => handleDownloadClubsWithAthletesPDF(downloadsEventId)}>
                        <Download size={14} /> Descargar PDF
                      </button>
                    </div>

                    {/* Planillas de Puntuación */}
                    <div className="od-dl-card od-dl-card-full">
                      <div className="od-dl-card-icon" style={{ '--dl-color': '#e55d36' }}><ClipboardCheck size={24} /></div>
                      <div className="od-dl-card-info">
                        <h4>Planillas de Puntuación para Jueces</h4>
                        <p>Una hoja por club y aparato. Atletas en orden alfabético con cuadrículas para evaluar y columna de Total.</p>
                        {downloadsEvent.apparatus && downloadsEvent.apparatus.length > 0 ? (
                          <div className="od-dl-apparatus-section">
                            <label className="od-dl-apparatus-label">Selecciona el aparato:</label>
                            <div className="od-dl-apparatus-list">
                              {downloadsEvent.apparatus.map(app => {
                                const appName = getApparatusName(app, downloadsEvent.discipline, downloadsEvent.category);
                                return (
                                  <button
                                    key={app}
                                    className={`od-dl-apparatus-chip ${selectedScoreApparatus === app ? 'active' : ''}`}
                                    onClick={() => setSelectedScoreApparatus(app)}
                                  >
                                    {appName}
                                  </button>
                                );
                              })}
                            </div>

                            {selectedScoreApparatus && (
                              <>
                                <div className="od-dl-filters">
                                  <div className="od-dl-filter-group">
                                    <label>Nivel:</label>
                                    <select value={selectedScoreLevel} onChange={e => setSelectedScoreLevel(e.target.value)} className="od-dl-filter-select">
                                      <option value="">Todos los niveles</option>
                                      {downloadsEventLevels.length > 0
                                        ? downloadsEventLevels.map(l => <option key={l.level_id || l.name} value={l.name}>{l.name}</option>)
                                        : [...new Set(downloadsClubSummary.flatMap(cs => cs.gymnasts.map(g => g.level)).filter(Boolean))].sort().map(l => <option key={l} value={l}>{l}</option>)
                                      }
                                    </select>
                                  </div>
                                  <div className="od-dl-filter-group">
                                    <label>Franja:</label>
                                    <select value={selectedScoreFranja} onChange={e => setSelectedScoreFranja(e.target.value)} className="od-dl-filter-select">
                                      <option value="">Todas las franjas</option>
                                      {[...new Set(downloadsClubSummary.flatMap(cs => cs.gymnasts.map(g => g.franja)).filter(Boolean))].sort().map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                  </div>
                                </div>

                                <button
                                  className="od-btn od-btn-download od-btn-download-accent"
                                  onClick={() => handleDownloadScoreSheets(selectedScoreApparatus)}
                                  style={{ marginTop: 12 }}
                                >
                                  <Download size={14} /> Descargar Planilla — {getApparatusName(selectedScoreApparatus)}
                                  {selectedScoreLevel ? ` · ${selectedScoreLevel}` : ''}
                                  {selectedScoreFranja ? ` · ${selectedScoreFranja}` : ''}
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <p className="od-dl-warn">⚠️ Esta copa no tiene aparatos configurados. Configúralos en la edición de la copa.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* ========================= RESULTS ========================= */}
          {activeSection === 'results' && (
            <>
              <p className="od-page-subtitle"><BarChart3 size={16} />Visualiza resultados, podios y estadísticas agregadas de tus copas.</p>
            <div className="od-card">
              <div className="od-card-header"><h2><BarChart3 size={20} className="icon-purple" /> Resultados y estadísticas</h2></div>
              <div className="od-card-body">
                <p className="od-desc">Selecciona una copa para ver resultados y gráficas.</p>
                <div className="od-results-selector">
                  <label>Seleccionar copa:</label>
                  <select value={resultsEventId} onChange={(e) => { setResultsEventId(e.target.value); handleLoadResults(e.target.value); }} className="od-input">
                    <option value="">-- Elige una copa --</option>
                    {myEvents.map(ev => <option key={ev.id} value={ev.id}>{ev.name} — {ev.date}</option>)}
                  </select>
                </div>

                {resultsLoading && <div className="od-empty"><div className="od-spinner" /><p>Cargando resultados...</p></div>}
                {!resultsEventId && !resultsLoading && (
                  <div className="od-empty"><BarChart3 size={48} strokeWidth={1} className="icon-purple" style={{ opacity: 0.4 }} /><p>Selecciona una copa</p></div>
                )}
                {resultsEventId && !resultsLoading && !analytics && resultsData && (
                  <div className="od-empty"><Trophy size={48} strokeWidth={1} className="icon-amber" style={{ opacity: 0.4 }} /><p>Sin datos de gimnastas aún</p></div>
                )}

                {analytics && (
                  <div className="od-results-content">

                    {/* Resultados PDF button */}
                    <div className="od-results-actions">
                      <button className="od-btn od-btn-download" onClick={handleDownloadResultsPDF} title="Exportar resultados a PDF">
                        <FileText size={15} /> Exportar Resultados PDF
                      </button>
                    </div>

                    <div className="od-results-stats">
                      <div className="od-stat-card" style={{'--stat-color':'#06b6d4'}}><Building2 size={24}/><span className="od-stat-val">{analytics.totalClubs}</span><span className="od-stat-label">Clubes</span></div>
                      <div className="od-stat-card" style={{'--stat-color':'#10b981'}}><Award size={24}/><span className="od-stat-val">{analytics.totalGymnasts}</span><span className="od-stat-label">Gimnasias</span></div>
                      <div className="od-stat-card" style={{'--stat-color':'#f59e0b'}}><Trophy size={24}/><span className="od-stat-val">{analytics.rankedAthletes[0]?.total||'—'}</span><span className="od-stat-label">Mejor Puntaje</span></div>
                      <div className="od-stat-card" style={{'--stat-color':'#7c3aed'}}><BarChart3 size={24}/><span className="od-stat-val">{analytics.clubStats[0]?.avg||'—'}</span><span className="od-stat-label">Mejor Promedio</span></div>
                    </div>

                    <div className="od-results-block">
                      <h3><Trophy size={20} className="icon-amber" /> Podio</h3>
                      <div className="od-podium">
                        {analytics.rankedAthletes.slice(0,3).map((a,i)=>(
                          <div key={a.id||i} className={`od-podium-item od-podium-${['gold','silver','bronze'][i]}`}>
                            <span className="od-podium-medal">{['🥇','🥈','🥉'][i]}</span>
                            <h4>{a.name||`${a.first_name||''} ${a.last_name||''}`.trim()}</h4>
                            <p className="od-podium-club">{a.clubName}</p>
                            <p className="od-podium-score"><strong>{a.total} pts</strong></p>
                            <p className="od-podium-avg">Prom: {a.avg}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="od-results-block">
                      <h3><BarChart3 size={20} className="icon-blue" /> Promedio por Club</h3>
                      <div className="od-chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={analytics.clubStats} margin={{top:5,right:30,left:20,bottom:5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/><XAxis dataKey="name" tick={{fontSize:12}} angle={-15} textAnchor="end" height={60}/><YAxis domain={[0,10]}/><Tooltip/>
                            <Bar dataKey="avg" name="Promedio" radius={[6,6,0,0]}>{analytics.clubStats.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {analytics.levelDist.length > 0 && (
                      <div className="od-results-block">
                        <h3><Award size={20} className="icon-purple" /> Distribución por Nivel</h3>
                        <div className="od-chart-container">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart><Pie data={analytics.levelDist} cx="50%" cy="50%" labelLine label={({name,percent})=>`${name} (${(percent*100).toFixed(0)}%)`} outerRadius={100} dataKey="value">
                              {analytics.levelDist.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Pie><Tooltip/></PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {analytics.apparatusAvg.length > 0 && (
                      <div className="od-results-block">
                        <h3><BarChart3 size={20} className="icon-green" /> Promedio por Aparato</h3>
                        <div className="od-chart-container">
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={analytics.apparatusAvg} margin={{top:5,right:30,left:20,bottom:5}}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/><XAxis dataKey="apparatus"/><YAxis domain={[0,10]}/><Tooltip/>
                              <Bar dataKey="avg" name="Promedio" radius={[6,6,0,0]} fill="#7c3aed"/>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    <div className="od-results-block">
                      <h3><Award size={20} className="icon-pink" /> Ranking Completo</h3>
                      <div className="od-events-table">
                        <table><thead><tr><th>#</th><th>Gimnasta</th><th>Club</th><th>Total</th><th>Promedio</th></tr></thead>
                          <tbody>{analytics.rankedAthletes.map((a,i)=>(
                            <tr key={a.id||i}><td>{i<3?['🥇','🥈','🥉'][i]:i+1}</td><td>{a.name||`${a.first_name||''} ${a.last_name||''}`.trim()}</td><td>{a.clubName}</td><td><strong>{a.total}</strong></td><td>{a.avg}</td></tr>
                          ))}</tbody>
                        </table>
                      </div>
                    </div>

                    <div className="od-results-block">
                      <h3><Calendar size={20} className="icon-blue" /> Resumen General</h3>
                      <div className="od-results-stats" style={{marginTop:12}}>
                        <div className="od-stat-card" style={{'--stat-color':'#3b82f6'}}><Calendar size={24}/><span className="od-stat-val">{myEvents.length}</span><span className="od-stat-label">Copas</span></div>
                        <div className="od-stat-card" style={{'--stat-color':'#10b981'}}><CheckCircle size={24}/><span className="od-stat-val">{myEvents.filter(e=>e.is_active).length}</span><span className="od-stat-label">Activos</span></div>
                        <div className="od-stat-card" style={{'--stat-color':'#06b6d4'}}><Building2 size={24}/><span className="od-stat-val">{accessRequests.filter(r=>r.status==='approved').length}</span><span className="od-stat-label">Participaciones</span></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </>
          )}
        </div>
      </main>

      {/* ========== Manage Event Modal ========== */}
      {managingEvent && (
        <div className="od-modal-overlay" onClick={() => setManagingEvent(null)}>
          <div className="od-modal od-modal-wide" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0 }}><Settings size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Administrar Copa</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#6b7280' }}>{managingEvent.name} — {managingEvent.date}</p>
              </div>
              <button onClick={() => setManagingEvent(null)} className="od-btn od-btn-ghost" style={{ padding: '4px 8px' }}><X size={18} /></button>
            </div>

            {managingLoading ? (
                          <div className="od-empty"><div className="od-spinner" /><p>Cargando...</p></div>
                        ) : managingRequests.length === 0 ? (
              <div className="od-empty" style={{ padding: 32 }}>
                <Building2 size={40} strokeWidth={1} style={{ opacity: 0.4, color: '#6b7280' }} />
                <p>No hay solicitudes para esta copa</p>
              </div>
            ) : (
              <>
                {/* Pending */}
                {managingRequests.filter(r => r.status === 'pending').length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: '0.9rem', color: '#f59e0b', margin: '0 0 8px' }}>⏳ Pendientes ({managingRequests.filter(r => r.status === 'pending').length})</h4>
                    {managingRequests.filter(r => r.status === 'pending').map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, border: '1px solid #fde68a', background: '#fffbeb', marginBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Building2 size={16} style={{ color: '#f59e0b' }} />
                          <div>
                            <strong style={{ fontSize: '0.88rem' }}>{req.clubs?.name || 'Club'}</strong>
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>{req.clubs?.email || ''} {req.clubs?.location ? `• ${req.clubs.location}` : ''}</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleManageApprove(req.id)} className="od-btn od-btn-approve od-btn-sm"><CheckSquare size={14} /> Aprobar</button>
                          <button onClick={() => handleManageReject(req.id)} className="od-btn od-btn-reject od-btn-sm"><XSquare size={14} /> Rechazar</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Approved */}
                {managingRequests.filter(r => r.status === 'approved').length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: '0.9rem', color: '#10b981', margin: '0 0 8px' }}>✅ Clubes Aprobados ({managingRequests.filter(r => r.status === 'approved').length})</h4>
                    {managingRequests.filter(r => r.status === 'approved').map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, border: '1px solid #bbf7d0', background: '#f0fdf4', marginBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Building2 size={16} style={{ color: '#10b981' }} />
                          <div>
                            <strong style={{ fontSize: '0.88rem' }}>{req.clubs?.name || 'Club'}</strong>
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>{req.clubs?.email || ''}</p>
                          </div>
                        </div>
                        <button onClick={() => handleRemoveClubFromEvent(req.id)} className="od-btn od-btn-reject od-btn-sm" title="Eliminar club de la copa"><UserMinus size={14} /> Eliminar</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Rejected */}
                {managingRequests.filter(r => r.status === 'rejected').length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '0.9rem', color: '#ef4444', margin: '0 0 8px' }}>❌ Rechazados ({managingRequests.filter(r => r.status === 'rejected').length})</h4>
                    {managingRequests.filter(r => r.status === 'rejected').map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, border: '1px solid #fecaca', background: '#fef2f2', marginBottom: 8, opacity: 0.7 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Building2 size={16} style={{ color: '#ef4444' }} />
                          <strong style={{ fontSize: '0.88rem' }}>{req.clubs?.name || 'Club'}</strong>
                        </div>
                        <span style={{ fontSize: '0.78rem', color: '#ef4444' }}>Rechazado</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ========== QR Modal ========== */}
      {qrModal && (
        <div className="od-modal-overlay" onClick={() => setQrModal(null)}>
          <div className="od-modal" onClick={e => e.stopPropagation()}>
            <h3>Código de Acceso</h3>
            <p className="od-modal-event">{qrModal.eventName}</p>
            <div className="od-qr-container"><QRCodeSVG value={qrModal.code} size={200} bgColor="#ffffff" fgColor="#1a1a1a" level="H" includeMargin /></div>
            <div className="od-code-display">
              <span className="od-code-text">{qrModal.code}</span>
              <button onClick={() => handleCopyCode(qrModal.code)} className="od-code-copy">{copiedCode === qrModal.code ? <CheckCircle size={18} className="icon-green" /> : <Copy size={18} />}</button>
            </div>
            {copiedCode === qrModal.code && <p className="od-copied-msg">¡Código copiado!</p>}
            <p className="od-modal-hint">Comparte este código o QR con los clubes para que soliciten participar.</p>
            <button onClick={() => setQrModal(null)} className="od-btn od-btn-ghost od-btn-block">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerDashboard;
